/**
 * useDeviceActions - Hook pour contrôler l'appareil
 *
 * Fonctionnalités:
 * - Minuteurs avec notifications
 * - Alarmes programmées
 * - Musique d'ambiance/focus
 * - Vibrations (mobile)
 * - Synthèse vocale
 * - Wake Lock (empêcher la veille)
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// Sons d'alarme intégrés (Base64 ou URLs)
const ALARM_SOUNDS = {
  gentle: '/sounds/gentle-alarm.mp3',
  energetic: '/sounds/energetic-alarm.mp3',
  nature: '/sounds/nature-alarm.mp3',
  default: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp6ZkYF2aGBqdH+IjY6NiYJ6c29xcnh+goODgX56d3Z3eXt9fn9/fn18e3p5eXl6e3x9fn5+fn19fHt7enp6e3x9fn5+fn19fHt7enp6e3x9fn5+fn19fHx7e3t7e3x8fX19fX19fHx8e3t7e3x8fX19fX19fHx8e3t7e3x8fH19fX19fXx8fHt7e3t8fHx9fX19fX18fHx7e3t7fHx8fX19fX19fHx8e3t7e3x8fH19fX19fXx8fHt7e3t8fHx9fX19fX18'
};

// Musiques d'ambiance (URLs YouTube/Spotify ou fichiers locaux)
const AMBIENT_MUSIC = {
  lofi: 'https://www.youtube.com/watch?v=jfKfPfyJRdk', // Lofi Girl
  nature: 'https://www.youtube.com/watch?v=eKFTSSKCzWA', // Rain sounds
  focus: 'https://www.youtube.com/watch?v=DIiUzYRPHZw', // Focus music
  white_noise: 'https://www.youtube.com/watch?v=nMfPqeZjc2c', // White noise
};

// Patterns de vibration
const VIBRATION_PATTERNS = {
  short: [100],
  double: [100, 50, 100],
  success: [100, 50, 100, 50, 200],
  alarm: [500, 200, 500, 200, 500],
  gentle: [50, 100, 50],
};

export function useDeviceActions() {
  // États
  const [timers, setTimers] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMusic, setCurrentMusic] = useState(null);
  const [wakeLockActive, setWakeLockActive] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');

  // Refs
  const audioRef = useRef(null);
  const wakeLockRef = useRef(null);
  const timerIntervalsRef = useRef({});
  const alarmTimeoutsRef = useRef({});

  // ============ PERMISSIONS ============

  // Demander la permission pour les notifications
  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('Notifications non supportées');
      return false;
    }

    if (Notification.permission === 'granted') {
      setNotificationPermission('granted');
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission === 'granted';
    }

    return false;
  }, []);

  // Vérifier les permissions au montage
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  // ============ NOTIFICATIONS ============

  const showNotification = useCallback((title, options = {}) => {
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        icon: '/logo192.png',
        badge: '/logo192.png',
        vibrate: VIBRATION_PATTERNS.double,
        requireInteraction: options.persistent || false,
        ...options
      });

      // Auto-fermer après 10 secondes si pas persistant
      if (!options.persistent) {
        setTimeout(() => notification.close(), 10000);
      }

      return notification;
    }
  }, []);

  // ============ MINUTEURS ============

  const setTimer = useCallback((minutes, label = 'Minuteur', options = {}) => {
    const id = `timer-${Date.now()}`;
    const endTime = Date.now() + minutes * 60 * 1000;

    const timer = {
      id,
      label,
      minutes,
      endTime,
      remaining: minutes * 60, // en secondes
      isPaused: false,
      createdAt: new Date().toISOString()
    };

    setTimers(prev => [...prev, timer]);

    // Intervalle pour mettre à jour le temps restant
    timerIntervalsRef.current[id] = setInterval(() => {
      setTimers(prev => {
        return prev.map(t => {
          if (t.id !== id || t.isPaused) return t;

          const remaining = Math.max(0, Math.floor((t.endTime - Date.now()) / 1000));

          if (remaining <= 0) {
            // Timer terminé
            clearInterval(timerIntervalsRef.current[id]);
            delete timerIntervalsRef.current[id];

            // Notification
            showNotification(`${label} terminé !`, {
              body: `Ton minuteur de ${minutes} minutes est fini.`,
              tag: id,
              persistent: true
            });

            // Son d'alarme
            playAlarmSound(options.sound || 'gentle');

            // Vibration
            vibrate('alarm');

            // Callback optionnel
            options.onComplete?.();

            return { ...t, remaining: 0, completed: true };
          }

          return { ...t, remaining };
        });
      });
    }, 1000);

    // Notification de confirmation
    if (options.notify !== false) {
      showNotification(`Minuteur démarré`, {
        body: `${label}: ${minutes} minutes`,
        tag: `${id}-start`
      });
    }

    return id;
  }, [showNotification]);

  const pauseTimer = useCallback((id) => {
    setTimers(prev => prev.map(t => {
      if (t.id === id) {
        if (t.isPaused) {
          // Reprendre
          const newEndTime = Date.now() + t.remaining * 1000;
          return { ...t, isPaused: false, endTime: newEndTime };
        } else {
          // Pause
          return { ...t, isPaused: true };
        }
      }
      return t;
    }));
  }, []);

  const cancelTimer = useCallback((id) => {
    if (timerIntervalsRef.current[id]) {
      clearInterval(timerIntervalsRef.current[id]);
      delete timerIntervalsRef.current[id];
    }
    setTimers(prev => prev.filter(t => t.id !== id));
  }, []);

  const clearAllTimers = useCallback(() => {
    Object.keys(timerIntervalsRef.current).forEach(id => {
      clearInterval(timerIntervalsRef.current[id]);
    });
    timerIntervalsRef.current = {};
    setTimers([]);
  }, []);

  // ============ ALARMES ============

  const setAlarm = useCallback((time, label = 'Alarme', options = {}) => {
    const id = `alarm-${Date.now()}`;
    const [hours, minutes] = time.split(':').map(Number);

    const now = new Date();
    const alarmTime = new Date();
    alarmTime.setHours(hours, minutes, 0, 0);

    // Si l'heure est passée, programmer pour demain
    if (alarmTime <= now) {
      alarmTime.setDate(alarmTime.getDate() + 1);
    }

    const delay = alarmTime.getTime() - now.getTime();

    const alarm = {
      id,
      label,
      time,
      alarmTime: alarmTime.toISOString(),
      isActive: true,
      createdAt: new Date().toISOString()
    };

    setAlarms(prev => [...prev, alarm]);

    // Programmer l'alarme
    alarmTimeoutsRef.current[id] = setTimeout(() => {
      // Notification
      showNotification(`${label}`, {
        body: `Il est ${time}`,
        tag: id,
        persistent: true,
        requireInteraction: true
      });

      // Son d'alarme (fort)
      playAlarmSound(options.sound || 'energetic', true);

      // Vibration répétée
      vibrateRepeated('alarm', 5);

      // Synthèse vocale
      if (options.speak !== false) {
        speak(`${label}. Il est ${hours} heures ${minutes > 0 ? `${minutes}` : ''}`);
      }

      // Callback
      options.onTrigger?.();

      // Marquer comme déclenchée
      setAlarms(prev => prev.map(a =>
        a.id === id ? { ...a, triggered: true, isActive: false } : a
      ));

      delete alarmTimeoutsRef.current[id];
    }, delay);

    // Confirmation
    showNotification(`Alarme programmée`, {
      body: `${label} à ${time}`,
      tag: `${id}-set`
    });

    return id;
  }, [showNotification]);

  const cancelAlarm = useCallback((id) => {
    if (alarmTimeoutsRef.current[id]) {
      clearTimeout(alarmTimeoutsRef.current[id]);
      delete alarmTimeoutsRef.current[id];
    }
    setAlarms(prev => prev.filter(a => a.id !== id));
  }, []);

  const snoozeAlarm = useCallback((id, minutes = 5) => {
    const alarm = alarms.find(a => a.id === id);
    if (!alarm) return;

    // Arrêter le son
    stopAlarmSound();

    // Reprogrammer
    const snoozeTime = new Date(Date.now() + minutes * 60 * 1000);
    const newTime = `${String(snoozeTime.getHours()).padStart(2, '0')}:${String(snoozeTime.getMinutes()).padStart(2, '0')}`;

    cancelAlarm(id);
    return setAlarm(newTime, `${alarm.label} (snooze)`, { speak: true });
  }, [alarms, setAlarm, cancelAlarm]);

  // ============ SONS ============

  const playAlarmSound = useCallback((type = 'gentle', loop = false) => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }

    const soundUrl = ALARM_SOUNDS[type] || ALARM_SOUNDS.default;
    audioRef.current.src = soundUrl;
    audioRef.current.loop = loop;
    audioRef.current.volume = 0.7;

    audioRef.current.play().catch(err => {
      console.warn('Impossible de jouer le son:', err);
    });
  }, []);

  const stopAlarmSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  // ============ MUSIQUE D'AMBIANCE ============

  const playAmbientMusic = useCallback((type = 'lofi') => {
    const musicUrl = AMBIENT_MUSIC[type];

    if (musicUrl.includes('youtube.com')) {
      // Ouvrir YouTube dans un nouvel onglet ou utiliser l'API embed
      window.open(musicUrl, '_blank');
      setCurrentMusic(type);
      setIsPlaying(true);
    } else {
      // Fichier local
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      audioRef.current.src = musicUrl;
      audioRef.current.loop = true;
      audioRef.current.volume = 0.3;
      audioRef.current.play();
      setCurrentMusic(type);
      setIsPlaying(true);
    }
  }, []);

  const stopMusic = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentMusic(null);
  }, []);

  // ============ VIBRATIONS ============

  const vibrate = useCallback((pattern = 'short') => {
    if ('vibrate' in navigator) {
      const vibrationPattern = VIBRATION_PATTERNS[pattern] || VIBRATION_PATTERNS.short;
      navigator.vibrate(vibrationPattern);
    }
  }, []);

  const vibrateRepeated = useCallback((pattern = 'alarm', times = 3) => {
    if ('vibrate' in navigator) {
      let count = 0;
      const interval = setInterval(() => {
        vibrate(pattern);
        count++;
        if (count >= times) {
          clearInterval(interval);
        }
      }, 1500);
    }
  }, [vibrate]);

  const stopVibration = useCallback(() => {
    if ('vibrate' in navigator) {
      navigator.vibrate(0);
    }
  }, []);

  // ============ SYNTHÈSE VOCALE ============

  const speak = useCallback((text, options = {}) => {
    if ('speechSynthesis' in window) {
      // Arrêter toute parole en cours
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = options.lang || 'fr-FR';
      utterance.rate = options.rate || 1;
      utterance.pitch = options.pitch || 1;
      utterance.volume = options.volume || 1;

      // Sélectionner une voix française si disponible
      const voices = window.speechSynthesis.getVoices();
      const frenchVoice = voices.find(v => v.lang.startsWith('fr'));
      if (frenchVoice) {
        utterance.voice = frenchVoice;
      }

      window.speechSynthesis.speak(utterance);
      return utterance;
    }
  }, []);

  const stopSpeaking = useCallback(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }, []);

  // ============ WAKE LOCK ============

  const requestWakeLock = useCallback(async () => {
    if ('wakeLock' in navigator) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        setWakeLockActive(true);

        wakeLockRef.current.addEventListener('release', () => {
          setWakeLockActive(false);
        });

        return true;
      } catch (err) {
        console.warn('Wake Lock non disponible:', err);
        return false;
      }
    }
    return false;
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release();
      wakeLockRef.current = null;
      setWakeLockActive(false);
    }
  }, []);

  // ============ ACTIONS RAPIDES POUR TÂCHES ============

  const startFocusSession = useCallback(async (task, minutes = 25) => {
    // Activer Wake Lock
    await requestWakeLock();

    // Démarrer le minuteur
    const timerId = setTimer(minutes, `Focus: ${task.task || task}`, {
      onComplete: () => {
        releaseWakeLock();
        speak(`Session de focus terminée. Tu as travaillé sur ${task.task || task}`);
      }
    });

    // Notification
    speak(`Début de session focus. ${minutes} minutes sur ${task.task || task}`);

    return timerId;
  }, [setTimer, requestWakeLock, releaseWakeLock, speak]);

  const setTaskReminder = useCallback((task, minutesBefore = 5) => {
    if (!task.time) return null;

    const [hours, minutes] = task.time.split(':').map(Number);
    const taskTime = new Date();
    taskTime.setHours(hours, minutes - minutesBefore, 0, 0);

    if (taskTime <= new Date()) return null;

    const reminderTime = `${String(taskTime.getHours()).padStart(2, '0')}:${String(taskTime.getMinutes()).padStart(2, '0')}`;

    return setAlarm(reminderTime, `Rappel: ${task.task}`, {
      onTrigger: () => {
        speak(`Dans ${minutesBefore} minutes: ${task.task}`);
      }
    });
  }, [setAlarm, speak]);

  // ============ CLEANUP ============

  useEffect(() => {
    return () => {
      // Nettoyer tous les timers
      Object.values(timerIntervalsRef.current).forEach(clearInterval);
      Object.values(alarmTimeoutsRef.current).forEach(clearTimeout);

      // Arrêter l'audio
      if (audioRef.current) {
        audioRef.current.pause();
      }

      // Libérer Wake Lock
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
      }
    };
  }, []);

  // ============ FORMATAGE ============

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return {
    // États
    timers,
    alarms,
    isPlaying,
    currentMusic,
    wakeLockActive,
    notificationPermission,

    // Permissions
    requestNotificationPermission,

    // Minuteurs
    setTimer,
    pauseTimer,
    cancelTimer,
    clearAllTimers,

    // Alarmes
    setAlarm,
    cancelAlarm,
    snoozeAlarm,

    // Sons
    playAlarmSound,
    stopAlarmSound,

    // Musique
    playAmbientMusic,
    stopMusic,
    AMBIENT_MUSIC,

    // Vibrations
    vibrate,
    vibrateRepeated,
    stopVibration,

    // Voix
    speak,
    stopSpeaking,

    // Wake Lock
    requestWakeLock,
    releaseWakeLock,

    // Actions tâches
    startFocusSession,
    setTaskReminder,

    // Utilitaires
    formatTime,
    showNotification,
  };
}

export default useDeviceActions;
